import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx';
import Tags from '@/componentsDS/Tags/Tags';
import EFormRenderer from '@/components/EFormV2/EFormRenderer';
import Loader from '@/componentsDS/Loader/Loader';
import { ToastContext } from '@/context/toast/index.js';
import usePage from '@/hooks/use-page';
import useConfig from '@/hooks/use-config/index';
import useSubmittalDetail from '@/hooks/use-submittals/useSubmittalDetail';
import { useNavigate, useParams } from '@/lib/router-dom';
import submittalsService from '@/services/api/submittalsService.js';
import { ReactComponent as CheckDouble } from '@/assets/svgs/DesignSystem/check-double.svg';
import { ReactComponent as CheckHistory } from '@/assets/svgs/DesignSystem/check-history.svg';
import { ReactComponent as XCircle } from '@/assets/svgs/DesignSystem/x-circle.svg';
import { ReactComponent as Clock } from '@/assets/svgs/DesignSystem/clock.svg';
import AccordionCard from '@/components/AccordionCard';
import { ReactComponent as IconChevronLeft } from '@/assets/svgs/chevron-left.svg';
import { ReactComponent as DownloadIcon } from '@/assets/svgs/download-icon-small.svg';
import { ReactComponent as EditIcon } from '@/assets/svgs/reference-edit.svg';
import { ReactComponent as NotificationIcon } from '@/assets/svgs/notification-solid.svg';
import SubmittalBanner from './components/SubmittalBanner';
import { buildLogEntries } from './utils/build-log-entries';
import ActivityLogModal from '@/componentsDS/ActivityLogModal';
import useModal from '@/hooks/use-modal/index';
import moment from 'moment';
import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import useSubmittalsVisibility from '@/hooks/use-submittals/useSubmittalsVisibility';
import { NON_EDITABLE_STATUSES, SUBMITTAL_STATUS } from './constants';

const STATUS_TAG_PROPS = {
    DRAFT:          { label: 'Draft',          prefixIcon: <CheckHistory />,      tagClassName: 'DS__tag--neutral'  },
    PENDING_REVIEW: { label: 'Pending Review', prefixIcon: <CheckHistory />,      tagClassName: 'DS__tag--warning'  },
    APPROVED:       { label: 'Approved',       prefixIcon: <CheckDouble />,       tagClassName: 'DS__tag--success'  },
    REJECTED:        { label: 'Rejected',         prefixIcon: <XCircle />, tagClassName: 'DS__tag--critical' },
    REPLACED:        { label: 'Replaced',         prefixIcon: <XCircle />, tagClassName: 'DS__tag--neutral'  },
    DELETED:         { label: 'Deleted',          prefixIcon: <Clock />,   tagClassName: 'DS__tag--neutral'  },
    PASSED_DEADLINE: { label: 'Passed Deadline',  prefixIcon: <Clock />,   tagClassName: 'DS__tag--critical' },
};

const SubmittalDetailPage = () => {
    const { showSubmittals } = useSubmittalsVisibility();
    const { provideItems } = useConfig();
    const { addToastMessage } = useContext(ToastContext);
    const { goTo } = useNavigate();
    const { submittal_id: submittalId } = useParams();
    const currentUserId = useSelector(state => state.auth.user?.id);

    const { submittal, schema, payload, setPayload, activityLogs, isLoading } = useSubmittalDetail({
        submittalId,
        addToastMessage,
    });

    const [isDownloading, setIsDownloading] = useState(false);
    const [isNotifying, setIsNotifying] = useState(false);
    const { visible: isActivityLogVisible, show: showActivityLog, hide: hideActivityLog } = useModal();

    const dataElement = provideItems.find(item => item.id === submittal?.data_element_id);
    const dataElementName = dataElement?.name || submittal?.data_element_id || '';

    const { breadcrumbsItems } = usePage({
        title: dataElementName || 'Submittal Detail',
        breadcrumbs: [
            { label: '', link: '/', isHomePage: true },
            { label: 'Submittals', link: '/submittals' },
            { label: 'Details' },
        ],
    });

    if (!showSubmittals) return null;

    const status = submittal?.status;
    const statusTag = STATUS_TAG_PROPS[status];
    const isOwner = !!submittal?.created_by && submittal.created_by === currentUserId;
    const isEditable = status && !NON_EDITABLE_STATUSES.has(status) && isOwner;
    const canNotifyApprover = status === SUBMITTAL_STATUS.PENDING_REVIEW;
    const projectContractName = submittal?.payload?.project_details?.project_contract_name;
    const submissionMonth = submittal?.submission_month || submittal?.payload?.project_details?.submission_month;
    const formattedSubmissionMonth = submissionMonth
        ? moment(submissionMonth, ['YYYY-MM', moment.ISO_8601]).format('MM/YYYY')
        : '';
    const lastUpdated = submittal?.modified ? moment(submittal.modified).format('DD MMM YYYY, h:mm A') : '';

    const handleEdit = () => {
        window.dataLayer?.push({
            event: 'submittals_detail_edit_form_clicked',
            data_element_name: dataElementName,
        });
        goTo(`/submittals/${submittalId}/edit`);
    };
    const handleBack = () => goTo('/submittals');
    const handleDownload = async () => {
        setIsDownloading(true);
        const [err] = await submittalsService.downloadSubmittal(submittalId, 'csv');
        if (err) {
            addToastMessage(err, 'error');
        } else {
            window.dataLayer?.push({
                event: 'submittals_detail_download',
                data_element_name: dataElementName,
            });
        }
        setIsDownloading(false);
    };

    const handleNotifyApprover = async () => {
        setIsNotifying(true);
        const [err] = await submittalsService.notifyApprover(submittalId);
        if (err) {
            addToastMessage(err, 'error');
        } else {
            addToastMessage('Approver has been notified successfully', 'success');
            window.dataLayer?.push({
                event: 'submittals_detail_notify_approver',
                data_element_name: dataElementName,
            });
        }
        setIsNotifying(false);
    };

    const handleViewActivityLog = () => {
        window.dataLayer?.push({
            event: 'submittals_detail_view_activity_log',
            data_element_name: dataElementName,
        });
        showActivityLog();
    };

    return (
        <div className="submittals-detail-page">
            <Breadcrumbs breadcrumbItems={breadcrumbsItems} />

            <div className="submittals-detail-page__header">
                <div className="submittals-detail-page__header-left">
                    <button
                        type="button"
                        className="submittals-detail-page__back-btn"
                        onClick={handleBack}
                        aria-label="Back to submittals"
                    >
                        <IconChevronLeft />
                    </button>
                    <div className="submittals-detail-page__header-text">
                        <div className="submittals-detail-page__title-row">
                            <h1 className="submittals-detail-page__title">{dataElementName}</h1>
                            {statusTag && <Tags {...statusTag} />}
                        </div>
                        <div className="submittals-detail-page__metadata">
                            {projectContractName && <span>Project Contract Name: {projectContractName}</span>}
                            {projectContractName && formattedSubmissionMonth && (
                                <span className="submittals-detail-page__sep">·</span>
                            )}
                            {formattedSubmissionMonth && <span>Submission Month: {formattedSubmissionMonth}</span>}
                        </div>
                        {lastUpdated && (
                            <div className="submittals-detail-page__last-updated">
                                Last updated on: {lastUpdated}
                            </div>
                        )}
                    </div>
                </div>
                <div className="submittals-detail-page__header-right">
                    {isEditable && (
                        <button
                            type="button"
                            className="submittals-detail-page__edit-btn"
                            onClick={handleEdit}
                        >
                            <EditIcon />
                            <span>{status === SUBMITTAL_STATUS.REJECTED ? 'Update Submission' : 'Edit Form'}</span>
                        </button>
                    )}
                    {canNotifyApprover && (
                        <button
                            type="button"
                            className="submittals-detail-page__edit-btn"
                            onClick={handleNotifyApprover}
                            disabled={isNotifying}
                        >
                            <NotificationIcon />
                            <span>Notify Approver Again</span>
                        </button>
                    )}
                    {status !== SUBMITTAL_STATUS.DRAFT && (
                        <button
                            type="button"
                            className="submittals-detail-page__download-btn"
                            onClick={handleDownload}
                            disabled={isDownloading}
                        >
                            <DownloadIcon />
                            <span>{isDownloading ? 'Downloading...' : 'Download'}</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="submittals-detail-page__content">
                {isLoading && (
                    <div className="submittals-detail-page__loading">
                        <Loader />
                    </div>
                )}
                {!isLoading && (
                    <>
                        <SubmittalBanner
                            status={status}
                            receiverOrgName={submittal?.receiver_org_name}
                            latestLog={activityLogs[0] ?? null}
                            onViewActivityLog={activityLogs.length > 0 ? handleViewActivityLog : null}
                        />
                        <AccordionCard
                            title={`${dataElementName} Details`}
                            defaultOpen
                            className="submittals-data-accordion"
                        >
                            <div className="eform-form">
                                <EFormRenderer
                                    schema={schema}
                                    payload={payload}
                                    setPayload={setPayload}
                                    isReadOnly
                                />
                            </div>
                        </AccordionCard>
                    </>
                )}
            </div>

            <ActivityLogModal
                visible={isActivityLogVisible}
                close={hideActivityLog}
                entries={buildLogEntries(activityLogs)}
            />
        </div>
    );
};

export default SubmittalDetailPage;
